import React, { useEffect, useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { ArrowUpDown, ChevronLeft, ChevronRight } from 'lucide-react';
import { dmy, inr, num, pct, qtyUnits, toneClass } from './format';

const PAGE = 50;

const Cell = ({ col, value }) => {
  const neg = value < 0 ? (col.key.includes('variance') ? 'text-emerald-700' : 'text-rose-600') : '';
  if (col.type === 'money') return <span className={neg}>{inr(value)}</span>;
  if (col.type === 'qty') return num(value);
  if (col.type === 'date') return dmy(value);
  if (col.type === 'units') return qtyUnits(value);
  if (col.type === 'pct') return <span className={neg}>{pct(value)}</span>;
  if (col.type === 'badge') {
    if (!value) return '—';
    return <span className={`inline-block px-2 py-0.5 rounded-full border text-[11px] font-medium whitespace-nowrap ${toneClass(value)}`}>{value}</span>;
  }
  return value === null || value === undefined || value === '' ? '—' : String(value);
};

const sortRows = (rows, sort) => {
  if (!sort.key) return rows;
  const dir = sort.dir === 'asc' ? 1 : -1;
  return [...rows].sort((a, b) => {
    const x = a[sort.key]; const y = b[sort.key];
    if (x === y) return 0;
    if (x === null || x === undefined || x === '') return 1;
    if (y === null || y === undefined || y === '') return -1;
    return (x > y ? 1 : -1) * dir;
  });
};

export const AnalyticsTable = ({ columns, rows, testId, rowKey }) => {
  const [page, setPage] = useState(0);
  const [sort, setSort] = useState({ key: null, dir: 'desc' });
  useEffect(() => { setPage(0); }, [rows]);
  const sorted = useMemo(() => sortRows(rows, sort), [rows, sort]);
  const pages = Math.max(1, Math.ceil(sorted.length / PAGE));
  const slice = sorted.slice(page * PAGE, page * PAGE + PAGE);
  const toggleSort = (key) => setSort((s) => ({ key, dir: s.key === key && s.dir === 'desc' ? 'asc' : 'desc' }));

  return (
    <div className="rounded-xl border border-slate-200 bg-white overflow-hidden" data-testid={testId}>
      <div className="overflow-auto max-h-[62vh]">
        <table className="w-full text-xs">
          <thead className="sticky top-0 z-10 bg-slate-900 text-slate-100">
            <tr>
              {columns.map((c) => (
                <th key={c.key} className="px-3 py-2.5 text-left font-semibold whitespace-nowrap">
                  <button type="button" className="flex items-center gap-1 hover:text-white" onClick={() => toggleSort(c.key)} data-testid={`${testId}-sort-${c.key}`}>
                    {c.label}<ArrowUpDown size={11} className="opacity-50" />
                  </button>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {slice.length === 0 && (
              <tr><td colSpan={columns.length} className="px-3 py-10 text-center text-slate-500" data-testid={`${testId}-empty`}>No records match the selected filters</td></tr>
            )}
            {slice.map((r, i) => (
              <tr key={rowKey ? rowKey(r, i) : i} className="border-t border-slate-100 hover:bg-indigo-50/40 transition-colors" data-testid={`${testId}-row`}>
                {columns.map((c) => (
                  <td key={c.key} className={`px-3 py-2 whitespace-nowrap text-slate-700 ${['money', 'qty', 'pct'].includes(c.type) ? 'text-right tabular-nums' : ''} ${c.key === 'description' || c.key === 'exception_remarks' ? 'max-w-xs truncate' : ''}`}
                    title={typeof r[c.key] === 'string' ? r[c.key] : undefined}>
                    <Cell col={c} value={r[c.key]} />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex items-center justify-between px-4 py-2 border-t border-slate-100 text-xs text-slate-600">
        <span data-testid={`${testId}-count`}>{sorted.length} records</span>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="ghost" disabled={page === 0} onClick={() => setPage(page - 1)} data-testid={`${testId}-prev`}><ChevronLeft size={14} /></Button>
          <span>Page {page + 1} / {pages}</span>
          <Button size="sm" variant="ghost" disabled={page >= pages - 1} onClick={() => setPage(page + 1)} data-testid={`${testId}-next`}><ChevronRight size={14} /></Button>
        </div>
      </div>
    </div>
  );
};
